import type { CleanupTypeVersion } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

/** "3 weeks ago", "yesterday", "Nov 2025" — compact recency for cards. */
export const formatLastUsed = (
  iso: string | null,
  now: Date = new Date()
): string | null => {
  if (!iso) return null;
  const then = new Date(iso);
  const days = Math.floor((now.getTime() - then.getTime()) / DAY_MS);
  if (days < 0) return 'upcoming';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  return then.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

/**
 * Full recency label for version cards. Future timestamps mean appointments
 * are already booked against the type — say that, not "last used upcoming".
 */
export const lastUsedLabel = (
  iso: string | null,
  now: Date = new Date()
): string | null => {
  if (!iso) return null;
  if (new Date(iso).getTime() > now.getTime()) {
    return 'has upcoming appointments';
  }
  return `last used ${formatLastUsed(iso, now)}`;
};

export const isStale = (
  iso: string | null,
  now: Date = new Date(),
  staleAfterDays = 90
): boolean => {
  if (!iso) return true;
  return now.getTime() - new Date(iso).getTime() > staleAfterDays * DAY_MS;
};

/**
 * Recency-aware caution for the volume-based suggestion (GDN-2226): fleet
 * analysis (2026-07-06) showed explicit deprecation terms are org-specific —
 * recency is the general signal. Fires when the suggested (most-used) winner
 * is stale while another version saw recent use.
 */
export const suggestionRecencyCaution = (
  versions: CleanupTypeVersion[],
  now: Date = new Date()
): { suggested: CleanupTypeVersion; fresher: CleanupTypeVersion } | null => {
  const suggested = versions[0];
  if (!suggested || !isStale(suggested.lastUsedAt, now)) return null;
  const fresher = versions.find(
    (v) => v.id !== suggested.id && !isStale(v.lastUsedAt, now)
  );
  return fresher ? { suggested, fresher } : null;
};

/**
 * Whole-group version of the caution: when NO version has seen recent use,
 * merging just launders a dead name into the shared catalog — archiving is
 * probably the right verb. 60-day window (tighter than the 90-day winner
 * caution) because a divergent group idle for two months across every
 * provider is already a strong drop signal.
 */
export const groupArchiveSuggestion = (
  versions: CleanupTypeVersion[],
  now: Date = new Date()
): { newestUse: string | null } | null => {
  if (versions.length === 0) return null;
  if (versions.some((v) => !isStale(v.lastUsedAt, now, 60))) return null;
  const dates = versions
    .map((v) => v.lastUsedAt)
    .filter((d): d is string => d !== null)
    .sort();
  return { newestUse: dates.at(-1) ?? null };
};

/**
 * Template-outlier detection for mixed groups: when a strong majority of a
 * group's versions share one template but a few point elsewhere, those few
 * are probably MISNAMED types, not duplicates ("Intake" on a progress-note
 * template). Merging them in would silently rewire a provider's workflow —
 * the wizard offers to set them aside for renaming instead. Thresholds:
 * groups of 4+, majority ≥75% (pilot data: 1 NPI group, 5 APA groups —
 * including APA's three biggest).
 */
export const templateOutliers = (
  versions: CleanupTypeVersion[],
  minSize = 4,
  majorityShare = 0.75
): { majorityTemplateTitle: string | null; outliers: CleanupTypeVersion[] } | null => {
  if (versions.length < minSize) return null;
  const byTemplate = new Map<string, CleanupTypeVersion[]>();
  for (const v of versions) {
    const key = v.templateId ?? 'none';
    byTemplate.set(key, [...(byTemplate.get(key) ?? []), v]);
  }
  if (byTemplate.size < 2) return null;
  const majority = [...byTemplate.values()].sort(
    (a, b) => b.length - a.length
  )[0];
  if (majority.length < versions.length * majorityShare) return null;
  const majorityKey = majority[0].templateId ?? 'none';
  return {
    majorityTemplateTitle: majority[0].templateTitle,
    outliers: versions.filter((v) => (v.templateId ?? 'none') !== majorityKey),
  };
};

/**
 * High-confidence junk markers only — fleet scan: "copy" (4,343 template
 * titles, 319 orgs) and "test" are universal; DNU/"do not use"/"old" are
 * rare but unambiguous when present. Date-stamps rank last: common in only
 * 10 orgs but unambiguous ("Follow Up Meds 6-12-25" was never meant to
 * outlive June). Soft caution, never a blocker.
 */
const TITLE_FLAGS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /\bdnu\b|do\s*not\s*use|don'?t\s*use/i, label: 'marked "do not use"' },
  { pattern: /\bdeprecated\b|\bobsolete\b/i, label: 'marked deprecated' },
  { pattern: /\bcopy\b/i, label: 'looks like a copy' },
  { pattern: /\btest\b/i, label: 'looks like a test' },
  { pattern: /\bold\b/i, label: 'marked old' },
  {
    pattern:
      /\b\d{1,2}[-/.]\d{1,2}[-/.](\d{2}|\d{4})\b|\b(jan|feb|mar|apr|jun|jul|aug|sept?|oct|nov|dec)[a-z]*\.?\s*\d{4}\b/i,
    label: 'has a date in the name',
  },
];

/**
 * Warning label when a title smells deprecated. Apply per-surface: a
 * version's templateTitle for card chips, the group's displayName for the
 * header (all versions in a group share the name — flagging each card on
 * the name would just repeat the same chip N times).
 */
export const flagTitle = (title: string | null): string | null => {
  if (!title) return null;
  for (const { pattern, label } of TITLE_FLAGS) {
    if (pattern.test(title)) return label;
  }
  return null;
};

const INTAKE_RE = /\bintake\b|\bnew\s+patient\b|\binitial\b|\bnew\s+adult\b/i;
const FOLLOWUP_RE =
  /follow[\s-]*up\b|\bf\/u\b|\bfu\b|\bprogress\b|\bmed(s)?\b|\bmed\s*check\b/i;

/** Coarse visit category from a name — for pair-sanity checks only. */
export const categorizeVisitName = (
  name: string | null
): 'intake' | 'followup' | null => {
  if (!name) return null;
  const intake = INTAKE_RE.test(name);
  const followup = FOLLOWUP_RE.test(name);
  if (intake === followup) return null; // both or neither → inconclusive
  return intake ? 'intake' : 'followup';
};

/**
 * True when a set of names spans more than one visit category (an intake
 * grouped with a follow-up) — the "interchangeable" claim is then suspect,
 * and the set should never be pre-checked to merge.
 */
export const namesSpanVisitCategories = (
  names: Array<string | null>
): boolean =>
  new Set(names.map(categorizeVisitName).filter(Boolean)).size >= 2;

/**
 * Pair-sanity check: a type named like a follow-up promoted with a template
 * named like an intake (or vice versa) is probably miswired — seen in real
 * pilot data ("Follow Up Meds 6-30-25" ⇄ "NEW patient evaluation 6/30/25").
 */
export const visitSemanticMismatch = (
  typeName: string,
  templateTitle: string | null
): {
  typeCategory: 'intake' | 'followup';
  templateCategory: 'intake' | 'followup';
} | null => {
  const typeCategory = categorizeVisitName(typeName);
  const templateCategory = categorizeVisitName(templateTitle);
  if (
    !typeCategory ||
    !templateCategory ||
    typeCategory === templateCategory
  ) {
    return null;
  }
  return { typeCategory, templateCategory };
};
